<template>
  <div class="visit-trend">
    <portal to="layout-nav-title">{{$t('wechatMiniProgram.analysis.visitTrend')}}</portal>

    <div>
      <h2>{{$t('wechatMiniProgram.analysis.daily')}}</h2>
      <el-date-picker
        v-model="dailyDate"
        type="date"
        @change="dailyDateChange"
        :picker-options="dailyPickerOptions"
        :placeholder="$t('wechatMiniProgram.analysis.pleaseSelectDate')">
      </el-date-picker>
      <bean-export-button v-bind="dailyExportButtonProps" style="margin-left: 20px;" />
      <Table
        :value="data['daily_visit_trend']"
        :columns="visitTrednColumns"
      />
    </div>

    <div>
      <h2>{{$t('wechatMiniProgram.analysis.weekly')}}</h2>
      <el-date-picker
        v-model="weeklyDate"
        type="week"
        :format="$t('wechatMiniProgram.analysis.weeklyFormat')"
        @change="weeklyDateChange"
        :picker-options="weeklyPickerOptions"
        :placeholder="$t('wechatMiniProgram.analysis.pleaseSelectWeek')">
      </el-date-picker>
      <bean-export-button v-bind="weeklyExportButtonProps" style="margin-left: 20px;" />
      <Table
        :value="data['weekly_visit_trend']"
        :columns="visitTrednColumns"
      />
    </div>

    <div>
      <h2>{{$t('wechatMiniProgram.analysis.monthly')}}</h2>
      <el-date-picker
        v-model="monthlyDate"
        type="month"
        @change="monthlyDateChange"
        :picker-options="monthlyPickerOptions"
        :placeholder="$t('wechatMiniProgram.analysis.pleaseSelectMonth')">
      </el-date-picker>
      <bean-export-button v-bind="monthlyExportButtonProps" style="margin-left: 20px;" />
      <Table
        :value="data['monthly_visit_trend']"
        :columns="visitTrednColumns"
      />
    </div>
  </div>
</template>

<script>
import { Vue, Component } from 'vue-property-decorator';
import moment from 'moment';
import Table from 'beans-admin-plugin/components/table';
import _ from 'lodash';

@Component({
  components: {
    Table
  }
})
export default class VisitTrend extends Vue {
  dailyDate = moment().add('days', -1);
  weeklyDate = moment().add('weeks', -1).endOf('week');
  monthlyDate = moment().add('months', -1).endOf('month');

  data = {
    daily_visit_trend: [],
    monthly_visit_trend: [],
    weekly_visit_trend: []
  }

  get dailyPickerOptions() {
    return {
      disabledDate: (time) => {
        return moment(time).diff(moment(new Date()), 'days') > -1
      }
    }
  }

  get weeklyPickerOptions() {
    return {
      disabledDate: (time) => {
        return moment(time).diff(moment(new Date()), 'weeks') > -1
      },
      // 一周开始日期，为周一日期
      firstDayOfWeek: 1
    }
  }

  get monthlyPickerOptions() {
    return {
      disabledDate: (time) => {
        return moment(time).diff(moment(new Date()), 'months') > -1
      }
    }
  }

  get visitTrednColumns() {
    return [
      {
        prop: 'ref_date',
        label: this.$t('wechatMiniProgram.analysis.refDate'),
        width: 160
      },
      {
        prop: 'session_cnt',
        label: this.$t('wechatMiniProgram.analysis.sessionCnt'),
        width: 100
      },
      {
        prop: 'visit_pv',
        label: this.$t('wechatMiniProgram.analysis.visitPv'),
        width: 100
      },
      {
        prop: 'visit_uv',
        label: this.$t('wechatMiniProgram.analysis.visitUv'),
        width: 100
      },
      {
        prop: 'visit_uv_new',
        label: this.$t('wechatMiniProgram.analysis.visitUvNew'),
        width: 100
      },
      {
        prop: 'stay_time_uv',
        label: this.$t('wechatMiniProgram.analysis.stayTimeUv'),
        width: 130
      },
      {
        prop: 'stay_time_session',
        label: this.$t('wechatMiniProgram.analysis.stayTimeSession'),
        width: 130
      },
      {
        prop: 'visit_depth',
        label: this.$t('wechatMiniProgram.analysis.visitDepth'),
        width: 120
      }
    ]
  }

  async mounted() {
    const [daily_visit_trend, weekly_visit_trend, monthly_visit_trend] = await Promise.all(
      [
        this.fetchDailyData(),
        this.fetchWeeklyData(),
        this.fetchMonthlyData()
      ]
    )
    this.data.daily_visit_trend = _.get(daily_visit_trend, 'list', []);
    this.data.weekly_visit_trend = _.get(weekly_visit_trend, 'list', []);
    this.data.monthly_visit_trend = _.get(monthly_visit_trend, 'list', []);
  }

  dateRangeParams(type) {
    if (type === 'daily') {
      const formatTime = moment(this.dailyDate).format('YYYY-MM-DD')
      return { begin_date: formatTime, end_date: formatTime };
    } else if (type === 'weekly') {
      return { begin_date: moment(this.weeklyDate).startOf('week').add('days', 1).format('YYYY-MM-DD'), end_date: moment(this.weeklyDate).endOf('week').add('days', 1).format('YYYY-MM-DD') }
    } else {
      return { begin_date: moment(this.monthlyDate).startOf('month').format('YYYY-MM-DD'), end_date: moment(this.monthlyDate).endOf('month').format('YYYY-MM-DD')  };
    }
  }

  async fetchDailyData() {
    if (this.dailyDate) {
      return await this.$request.get(`applications/${'wx8bb4a20e2f302ca3'}/analyses/getweanalysisappiddailyvisittrend`, this.dateRangeParams('daily'));
    } else {
      this.$message.warning(this.$t('wechatMiniProgram.analysis.pleaseSelectDate'));
    }
  }

  async fetchWeeklyData() {
    if (this.weeklyDate) {
      return await this.$request.get(`applications/${'wx8bb4a20e2f302ca3'}/analyses/getweanalysisappidweeklyvisittrend`, this.dateRangeParams('weekly'));
    } else {
      this.$message.warning(this.$t('wechatMiniProgram.analysis.pleaseSelectWeek'));
    }
  }

  async fetchMonthlyData() {
    if (this.monthlyDate) {
      return await this.$request.get(`applications/${'wx8bb4a20e2f302ca3'}/analyses/getweanalysisappidmonthlyvisittrend`, this.dateRangeParams('monthly'));
    } else {
      this.$message.warning(this.$t('wechatMiniProgram.analysis.pleaseSelectMonth'));
    }
  }

  async dailyDateChange() {
    const data = await this.fetchDailyData();
    this.data.daily_visit_trend = _.get(data, 'list', []);
  }

  async weeklyDateChange() {
    const data = await this.fetchWeeklyData();
    this.data.weekly_visit_trend = _.get(data, 'list', []);
  }

  async monthlyDateChange() {
    const data = await this.fetchMonthlyData();
    this.data.monthly_visit_trend = _.get(data, 'list', []);
  }

  get dailyExportButtonProps() {
    return {
      url: '/export_analyses/getweanalysisappiddailyvisittrend',
      params: this.dateRangeParams('daily'),
      fileName: `${this.$t('wechatMiniProgram.analysis.visitTrend')}-${this.$t('wechatMiniProgram.analysis.daily')}.xlsx`,
    }
  }

  get weeklyExportButtonProps() {
    return {
      url: '/export_analyses/getweanalysisappidweeklyvisittrend',
      params: this.dateRangeParams('weekly'),
      fileName: `${this.$t('wechatMiniProgram.analysis.visitTrend')}-${this.$t('wechatMiniProgram.analysis.weekly')}.xlsx`,
    }
  }

  get monthlyExportButtonProps() {
    return {
      url: '/export_analyses/getweanalysisappidmonthlyvisittrend',
      params: this.dateRangeParams('monthly'),
      fileName: `${this.$t('wechatMiniProgram.analysis.visitTrend')}-${this.$t('wechatMiniProgram.analysis.monthly')}.xlsx`,
    }
  }
}

export const route = [
  {
    path: '/visit_trend',
    name: 'visit_trend',
    component: VisitTrend,
    meta: {
      can: 'data_analyses.visit_trend'
    }
  }
]
</script>

<style lang="scss" scoped>
.visit-trend {
  margin-left: 20px;

  .el-table {
    margin: 20px 0;
    max-width: 940px;
  }
}
</style>
